import ApiService from './ApiService';
import { executiveResourceKey, ExecutiveResourceMap } from '@stores/chatbot/executiveSession';
import { attachAuthHeaders } from './authHeaders';
import { fetchWithSessionRefresh } from './sessionRefreshFetch';
import { API_BASE_URL } from '@config/config';
import { isExecutiveConversationId, parseSteerAck, type ExecutiveSteerAck } from './executiveContract';
import { formatError } from '@utils/FormatError';

export type AnnotationVerdict = 'good' | 'bad';

export interface AnnotationSpan {
  start: number;
  end: number;
  quote?: string;
  verdict: AnnotationVerdict | null; // null = a neutral comment
  comment: string;
}

export interface ResponseAnnotationInput {
  message_text: string;
  spans: AnnotationSpan[];
  overall_rating: number | null; // 1-5 stars
  note: string;
}

export interface ResponseAnnotation extends ResponseAnnotationInput {
  message_timestamp: string;
  annotator_user_id: string;
  annotator_email?: string | null;
  updated_at: string;
  is_mine: boolean;
}

const executiveSteerControllers = new ExecutiveResourceMap<AbortController>(controller => controller.abort());

export interface CreateNewConversationRequest {
  message: string;
  conversation_id?: string;
  enable_streaming?: boolean;
  client_requirements_note?: string;
  is_test?: boolean;
  uploaded_images?: any[];
}

export interface NewConversationResponse200 {
  response: any;
  conversation_id: string;
  conversation_title?: string;
  streaming?: boolean;
  response_type?: string;
  confidence?: number;
  tool_calls_count?: number;
  message_count?: number;
  operation_id?: string;
  timestamp?: string;
}

export interface NewConversationResponse202 {
  response: {
    status: 'pending_approval';
    conversation_id: string;
    message: string;
    approval_data: any;
  };
}

export type NewConversationResponse =
  | NewConversationResponse200
  | NewConversationResponse202;

export interface NewConversationApiResult {
  data: NewConversationResponse;
  status: number | undefined;
}

export interface NewConversationListItem {
  conversation_id: string;
  title: string;
  timestamp: number;
  last_activity_at?: number;
  started_by_user_name?: string;
  // Where this conversation originated. "slack" when a Leo @-mention spawns
  // the conversation in Slack; "mcp" when it's started from the Claude app
  // through the MCP connector. Absent means it was started in the web UI.
  origin?: string;
  // Staff had this chat under masquerade. Only sent to a masquerading caller.
  masquerade?: boolean;
}

// Default page size for the paginated history listing. Small enough that the
// first render only loads a handful of conversation bodies (title lives in the
// body), large enough to fill the panel; more load on scroll.
export const DEFAULT_HISTORY_PAGE_SIZE = 15;

// One page of the paginated /new-conversations response. `next_cursor` is an
// opaque token to pass back for the following page; null means no more pages.
export interface NewConversationPage {
  items: NewConversationListItem[];
  next_cursor: string | null;
}

/**
 * SSE stream event with event ID for reconnection support.
 * The eventId corresponds to the SSE `id:` field and is used with
 * the `Last-Event-ID` header to resume streams after connection drops.
 */
export interface SSEStreamEvent<T = any> {
  /** SSE event ID (sequence number) for reconnection tracking */
  eventId: number;
  /** The parsed event data from the SSE `data:` field */
  data: T;
}

// PROVISIONAL(#7443) — see docs/PROVISIONAL_7443.md.
// A connection that dies without a FIN — the usual shape of a middlebox or
// load-balancer drop — leaves `reader.read()` pending forever, so the turn
// hangs with no error and no timeout anywhere on the path. The server emits a
// ": keepalive" every 30s (src/utils/redis_stream.py KEEPALIVE_INTERVAL), so
// four missed ones mean the connection is gone rather than the agent slow.
// Only armed once a keepalive has actually been seen (see readWithStallTimeout),
// so a server that makes no such promise is never cut short. Raise this value
// to retreat without reverting.
const SSE_STALL_TIMEOUT_MS = 120000;

class NewConversationService {
  /**
   * Extract error message and code from a failed response
   */
  private async extractErrorFromResponse(
    response: Response,
    defaultMessage: string
  ): Promise<{ message: string; code?: string }> {
    try {
      const errorText = await response.text();
      if (!errorText) {
        return { message: defaultMessage };
      }

      try {
        const errorData = JSON.parse(errorText);
        return {
          message: errorData.error || errorData.message || defaultMessage,
          code: errorData.code,
        };
      } catch {
        // If not JSON, use the text as-is if it's meaningful
        return {
          message: errorText.trim().length > 0 && errorText.length < 500
            ? errorText.trim()
            : defaultMessage,
        };
      }
    } catch (parseError) {
      console.error('❌ [NewConversationService] Error reading error response:', parseError);
      return { message: defaultMessage };
    }
  }

  public async create(
    body: CreateNewConversationRequest,
  ): Promise<NewConversationApiResult> {
    const url = body.conversation_id
      ? `/new-conversations/${body.conversation_id}`
      : `/new-conversations`;
    
    // Initiating streaming request
    const absoluteUrl = `${API_BASE_URL}${url}`;
    
    // Always use streaming request handler since backend always streams
    const streamingResult = await this.createStreamingRequest(absoluteUrl, body);
    return streamingResult;
  }

  public async createStreaming(
    body: CreateNewConversationRequest,
    onChunk?: (data: any) => void,
    onComplete?: (data: any) => void,
    onError?: (error: string) => void
  ): Promise<NewConversationApiResult> {
    const url = body.conversation_id
      ? `/new-conversations/${body.conversation_id}`
      : `/new-conversations`;
    
    const absoluteUrl = `${API_BASE_URL}${url}`;
    
    return await this.createStreamingRequest(absoluteUrl, body, onChunk, onComplete, onError);
  }

  private async createStreamingRequest(
    url: string,
    body: CreateNewConversationRequest,
    onChunk?: (data: any) => void,
    onComplete?: (data: any) => void,
    onError?: (error: string) => void
  ): Promise<NewConversationApiResult> {
    return new Promise((resolve, reject) => {
      let streamCompleted = false;
      let lastStreamData: any = null;

      // Resettable timeout — resets every time we receive ANY data from the
      // stream, including SSE keepalive comments (": keepalive\n\n").
      // This prevents the timeout from firing during long-running agent
      // operations where the backend is alive and sending keepalives every 30s.
      const STREAM_TIMEOUT_MS = 120000; // 120 seconds of NO activity
      let streamTimeoutId: ReturnType<typeof setTimeout>;

      const resetStreamTimeout = () => {
        clearTimeout(streamTimeoutId);
        if (streamCompleted) return;
        streamTimeoutId = setTimeout(() => {
          if (!streamCompleted) {
            console.warn('🕐 [NewConversationService] Stream timeout — no data received for 2 minutes');

            streamCompleted = true;

            // If we have any stream data, try to use it instead of failing completely
            if (lastStreamData && (lastStreamData.conversation_id || lastStreamData.response)) {
              resolve({
                data: {
                  conversation_id: lastStreamData.conversation_id || `timeout_${Date.now()}`,
                  response: lastStreamData.response || 'Request timed out but may have partial response. Please try again.',
                  streaming: true
                },
                status: 200
              });
            } else {
              console.error('❌ [NewConversationService] No stream data available after timeout');
              reject(new Error('Stream timeout - no response received within 2 minutes. This may be due to server overload or connection issues.'));
            }
          }
        }, STREAM_TIMEOUT_MS);
      };

      // Start the initial timeout
      resetStreamTimeout();

      // Raw fetch bypasses the axios interceptor, so pull the shared
      // masquerade + per-tab workspace headers from attachAuthHeaders.
      const headers = attachAuthHeaders({
        'Content-Type': 'application/json',
        'Accept': 'text/event-stream',
        'Cache-Control': 'no-cache',
      });

      fetch(url, {
        method: 'POST',
        headers,
        credentials: 'include',
        body: JSON.stringify(body),
      })
      .then(async (response) => {
        if (!response.ok) {
          clearTimeout(streamTimeoutId);
          console.error('❌ [API ERROR]', response.status, response.statusText);
          
          const { message, code } = await this.extractErrorFromResponse(
            response,
            `HTTP ${response.status}`
          );
          
          const error = new Error(message);
          if (code) {
            (error as any).code = code;
          }
          
          if (onError) {
            onError(message);
          }
          throw error;
        }

        if (response.headers.get('content-type')?.includes('text/event-stream')) {
          // Streaming response detected — pass resetStreamTimeout so the
          // timeout resets on every chunk (including keepalives)
          const eventSource = this.createEventSourceFromResponse(response, resetStreamTimeout);

          let finalResult: any = null;

          eventSource.addEventListener('message', (event) => {
            try {
              const data = JSON.parse(event.data);
              lastStreamData = data;

              if (data.type === 'complete') {
                streamCompleted = true;
                clearTimeout(streamTimeoutId);
                finalResult = data;

                // Call onComplete callback if provided
                if (onComplete) {
                  onComplete(data);
                }

                eventSource.close();
                
                // Ensure we have valid data before resolving
                if (finalResult.data) {
                  resolve({
                    data: finalResult.data,
                    status: response.status
                  });
                } else {
                  console.error('❌ [COMPLETE] No data in complete event');
                  reject(new Error('Stream completed but no data received'));
                }
              } else if (data.type === 'error') {
                console.error('❌ [ERROR]', data.message);
                streamCompleted = true;
                clearTimeout(streamTimeoutId);
                eventSource.close();
                
                // Call onError callback if provided
                if (onError) {
                  onError(data.message || 'Streaming error');
                }
                
                reject(new Error(data.message || 'Streaming error'));
              } else {
                // Handle other event types (chunk, tools_loading, etc.) via callbacks
                // Call onChunk for all non-complete, non-error events
                if (onChunk) {
                  onChunk(data);
                }
              }
              
              // Always call handleStreamingEvent for CustomEvent dispatch (backward compatibility)
              this.handleStreamingEvent(data);
              
            } catch (e) {
              console.error('❌ [NewConversationService] Error parsing event data:', e);
              streamCompleted = true;
              clearTimeout(streamTimeoutId);
              eventSource.close();
              
              const errorMessage = 'Invalid streaming data format';
              if (onError) {
                onError(errorMessage);
              }
              
              reject(new Error(errorMessage));
            }
          });

          eventSource.addEventListener('error', (errorEvent) => {
            console.error('❌ [NewConversationService] EventSource error:', errorEvent);
            streamCompleted = true;
            clearTimeout(streamTimeoutId);
            eventSource.close();
            
            const errorMessage = 'Streaming connection failed';
            if (onError) {
              onError(errorMessage);
            }
            
            reject(new Error(errorMessage));
          });
          
        } else {
          clearTimeout(streamTimeoutId);
          // Regular JSON response
          const data = await response.json();
          resolve({ data, status: response.status });
        }
      })
      .catch((error) => {
        clearTimeout(streamTimeoutId);
        console.error('❌ [NewConversationService] Fetch error:', error);
        
        // Provide more user-friendly error messages for common network issues
        let userFriendlyMessage = error.message;
        if (error.message?.includes('ERR_HTTP2_PROTOCOL_ERROR')) {
          userFriendlyMessage = 'Connection issue with server (HTTP/2 protocol error). Please try again.';
        } else if (error.message?.includes('fetch')) {
          userFriendlyMessage = 'Network connection failed. Please check your internet connection and try again.';
        } else if (error.message?.includes('timeout')) {
          userFriendlyMessage = 'Request timed out. The server may be busy, please try again in a few moments.';
        }
        
        if (onError) {
          onError(userFriendlyMessage);
        }
        
        reject(new Error(userFriendlyMessage));
      });
    });
  }


  private createEventSourceFromResponse(response: Response, onActivity?: () => void): EventSource {
    // Note: This is a simplified approach. In practice, you might need
    // to handle the stream manually since EventSource has limitations
    // with POST requests and custom headers
    const reader = response.body?.getReader();
    const decoder = new TextDecoder();

    // Create a mock EventSource-like object
    const mockEventSource = {
      addEventListener: (type: string, listener: (event: any) => void) => {
        if (type === 'message' && reader) {
          this.processStream(reader, decoder, listener, onActivity);
        }
      },
      close: () => {
        reader?.cancel();
      }
    };

    return mockEventSource as any;
  }

  private async processStream(
    reader: ReadableStreamDefaultReader<Uint8Array>,
    decoder: TextDecoder,
    messageListener: (event: any) => void,
    onActivity?: () => void
  ) {
    let buffer = '';

    try {
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        // Signal activity on ANY data received — including SSE keepalive
        // comments (": keepalive\n\n"). This resets the stream timeout so
        // long-running agent operations don't get killed prematurely.
        onActivity?.();

        const chunk = decoder.decode(value, { stream: true });
        buffer += chunk;
        
        // Process complete lines, keeping incomplete ones in buffer
        const lines = buffer.split('\n');
        buffer = lines.pop() || ''; // Keep the last (potentially incomplete) line in buffer

        for (const line of lines) {
          if (line.startsWith('data: ')) {
            const data = line.slice(6).trim();
            if (data && data !== '[]') { // Filter out empty array separators
              try {
                // Validate that it's proper JSON before processing
                const parsedData = JSON.parse(data);
                
                // Enhanced filtering: Skip tool call debug data even at this level
                if (parsedData.type === 'chunk' && parsedData.data) {
                  const chunkContent = parsedData.data.trim();
                  // Filter out tool call metadata that shouldn't be shown to users
                  if (chunkContent.startsWith('[{') &&
                      (chunkContent.includes('toolu_') ||
                       chunkContent.includes('partial_json') ||
                       chunkContent.includes('input_json_delta'))) {
                    continue; // Skip this chunk
                  }
                }
                
                messageListener({ data: data } as MessageEvent);
              } catch (parseError) {
                console.warn('Skipping malformed JSON chunk:', data.slice(0, 100));
              }
            }
          }
        }
      }
      
      // Process any remaining data in buffer
      if (buffer.trim()) {
        const line = buffer.trim();
        if (line.startsWith('data: ')) {
          const data = line.slice(6).trim();
          if (data && data !== '[]') {
            try {
              const parsedData = JSON.parse(data);
              
              // Enhanced filtering: Skip tool call debug data even at this level  
              if (parsedData.type === 'chunk' && parsedData.data) {
                const chunkContent = parsedData.data.trim();
                // Filter out tool call metadata that shouldn't be shown to users
                if (chunkContent.startsWith('[{') &&
                    (chunkContent.includes('toolu_') ||
                     chunkContent.includes('partial_json') ||
                     chunkContent.includes('input_json_delta'))) {
                  return; // Skip this chunk
                }
              }
              
              messageListener({ data: data } as MessageEvent);
            } catch (parseError) {
              console.warn('Skipping malformed JSON in final buffer:', data.slice(0, 100));
            }
          }
        }
      }
    } catch (error) {
      console.error('Stream processing error:', error);
    }
  }

  private handleStreamingEvent(data: any) {
    // Emit custom events or call callbacks for different streaming events
    // This allows the UI to respond to streaming progress
    const event = new CustomEvent('conversationStreaming', {
      detail: data
    });
    window.dispatchEvent(event);
  }

  public async approve(conversationId: string, body: any): Promise<any> {
    const { success, data, error } = await ApiService.post<any, any>(
      `/new-conversations/${conversationId}/approval`,
      body,
    );
    if (success) return data;
    throw new Error(String(error) || 'NewConversationService::approve failed');
  }

  /**
   * Submit questionnaire answers and stream the agent's continuation response.
   * The backend resumes the interrupted LangGraph and streams SSE events.
   */
  public async *streamQuestionnaire(
    conversationId: string,
    answers: Array<{ question_id: string; selected_option: string | null; freeform_text: string | null }>,
    signal?: AbortSignal,
  ): AsyncGenerator<SSEStreamEvent, void, unknown> {
    const { API_BASE_URL } = await import('@config/config');
    const url = `${API_BASE_URL}/new-conversations/${conversationId}/questionnaire`;

    const headers = attachAuthHeaders({ 'Content-Type': 'application/json' });

    const response = await fetchWithSessionRefresh(
      () => fetch(url, {
        method: 'POST',
        headers,
        credentials: 'include',
        body: JSON.stringify({ answers }),
        signal,
      }),
      () => ApiService.refreshSession(),
    );

    if (!response.ok) {
      const { message, code } = await this.extractErrorFromResponse(
        response,
        `Questionnaire stream failed: ${response.status}${response.statusText ? ` ${response.statusText}` : ''}`
      );
      const error = new Error(message);
      if (code) {
        (error as any).code = code;
      }
      (error as any).status = response.status;
      throw error;
    }

    const reader = response.body?.getReader();
    const decoder = new TextDecoder();
    let buffer = '';
    let currentEventId = 0;

    if (!reader) {
      throw new Error('No reader available');
    }

    while (true) {
      // Deliberately NOT readWithStallTimeout: this endpoint streams straight
      // from the agent (_invoke_project_manager_lambda) with no keepalive
      // emitter, so silence here means a slow tool call, not a dead socket.
      // The stall deadline only holds where the 30s keepalive contract does.
      const { done, value } = await reader.read();
      if (done) break;

      buffer += decoder.decode(value, { stream: true });
      const lines = buffer.split('\n');
      buffer = lines.pop() || '';

      for (const line of lines) {
        if (line.startsWith('id: ')) {
          const parsedId = parseInt(line.slice(4), 10);
          if (!isNaN(parsedId)) {
            currentEventId = parsedId;
          }
        } else if (line.startsWith(':')) {
          yield { eventId: currentEventId, data: { type: 'keepalive' } };
        } else if (line.startsWith('data: ')) {
          try {
            const data = JSON.parse(line.slice(6));
            yield { eventId: currentEventId, data };
          } catch (e) {
            console.warn('Failed to parse SSE data:', line, e);
          }
        }
      }
    }
  }

  public async getAll(): Promise<NewConversationListItem[]> {
    const { success, data, error } = await ApiService.get<
      NewConversationListItem[]
    >('/new-conversations');
    if (success) return (data as unknown) as NewConversationListItem[];
    throw new Error(String(error) || 'NewConversationService::getAll failed');
  }

  /**
   * Fetch one page of conversation history, newest-activity first. Sending
   * `limit` opts the backend into its paginated shape: titles are S3-loaded
   * for only this page, so cost is bounded regardless of history size. Pass
   * the previous page's `next_cursor` to resume; a null cursor means the end.
   */
  public async getPage(params: {
    limit?: number;
    cursor?: string | null;
  } = {}): Promise<NewConversationPage> {
    const query: Record<string, string | number> = {
      limit: params.limit ?? DEFAULT_HISTORY_PAGE_SIZE,
    };
    if (params.cursor) query.cursor = params.cursor;
    const { success, data, error } = await ApiService.get<NewConversationPage>(
      '/new-conversations',
      query,
    );
    if (success) return (data as unknown) as NewConversationPage;
    throw new Error(String(error) || 'NewConversationService::getPage failed');
  }

  /**
   * `//context` debug view — fetch the Code Mode system prompt Leo WOULD be
   * handed on the NEXT turn of this conversation, computed live. Internal-staff
   * only (gated server-side). `captured: false` ⇒ preview couldn't be built.
   */
  public async getContext(conversationId: string): Promise<{
    captured: boolean;
    systemPrompt: string;
    charCount?: number;
    message?: string;
  }> {
    const { success, data } = await ApiService.get<{
      captured?: boolean;
      system_prompt?: string;
      char_count?: number;
      message?: string;
    }>(`/new-conversations/${conversationId}/context`);
    if (success && data) {
      return {
        captured: Boolean(data.captured),
        systemPrompt: data.system_prompt || '',
        charCount: data.char_count,
        message: data.message,
      };
    }
    return { captured: false, systemPrompt: '', message: 'Failed to load context.' };
  }

  /**
   * `//repo` debug view — fetch the expert-client repo code-mode Leo WOULD
   * mount on the NEXT turn (deterministic base + this company's script overlay,
   * account files, and data/ working memory), computed live. Internal-staff
   * only (gated server-side). `captured: false` ⇒ preview couldn't be built.
   */
  public async getRepo(conversationId: string): Promise<{
    captured: boolean;
    files: Record<string, { content: string; size: number; truncated: boolean; origin?: string; diff?: string }>;
    counts?: Record<string, number>;
    message?: string;
  }> {
    const { success, data } = await ApiService.get<{
      captured?: boolean;
      files?: Record<string, { content: string; size: number; truncated: boolean; origin?: string; diff?: string }>;
      counts?: Record<string, number>;
      message?: string;
    }>(`/new-conversations/${conversationId}/repo`);
    if (success && data) {
      return {
        captured: Boolean(data.captured),
        files: data.files || {},
        counts: data.counts,
        message: data.message,
      };
    }
    return { captured: false, files: {}, message: 'Failed to load repo.' };
  }

  public async getById(conversationId: string, historyCursor?: string): Promise<any> {
    const { success, data, error, status } = await ApiService.get<any>(
      `/new-conversations/${conversationId}`,
      historyCursor ? { history_cursor: historyCursor, history_limit: 50 } : undefined,
    );
    if (success) return data as any;
    // History viewers must distinguish revoked access from a transient refresh failure.
    throw Object.assign(
      new Error(String(error || 'NewConversationService::getById failed')),
      { status },
    );
  }

  public async rename(conversationId: string, title: string): Promise<{ conversation_id: string; title: string }> {
    const { success, data, error } = await ApiService.patch<
      { title: string },
      { conversation_id: string; title: string }
    >(`/new-conversations/${conversationId}`, { title });
    if (success && data) return data;
    throw new Error(String(error) || 'NewConversationService::rename failed');
  }

  public async delete(conversationId: string): Promise<void> {
    const { success, error } = await ApiService.delete(
      `/new-conversations/${conversationId}`,
    );
    if (!success) {
      throw new Error(String(error) || 'NewConversationService::delete failed');
    }
  }

  /**
   * Signal cooperative cancel for a streaming conversation (worker mode).
   * @returns true if cancel was accepted (worker env), false if endpoint returned error (e.g. workers not enabled).
   */
  public async cancelStream(conversationId: string): Promise<boolean> {
    try {
      const { success, error } = await ApiService.post<undefined, { cancel_requested?: boolean }>(
        `/new-conversations/${conversationId}/cancel`,
        undefined,
      );
      if (!success) {
        console.warn('[NewConversationService.cancelStream] Cancel request failed:', error);
        return false;
      }
      return true;
    } catch (e) {
      console.warn('[NewConversationService.cancelStream] Error calling cancel endpoint:', e);
      return false;
    }
  }

  /**
   * The conversation inbox: a message sent while a Code Mode turn runs, read
   * by that turn after its next code block. Returns the queued id, or the
   * server's refusal text (empty, too long, too many waiting).
   */
  public async postInbox(conversationId: string, message: string): Promise<{ id?: string; error?: string }> {
    const { success, data, error } = await ApiService.post<{ message: string }, { id?: string; queued?: boolean }>(
      `/new-conversations/${conversationId}/inbox`,
      { message },
    );
    if (success && data?.id) return { id: data.id };
    return { error: error || 'This message could not be sent.' };
  }

  /** The inbox messages no turn has read yet, oldest first; null when the read failed. */
  public async getInbox(conversationId: string): Promise<Array<{ id: string; message: string }> | null> {
    const { success, data } = await ApiService.get<{ pending?: Array<{ id: string; message: string }> }>(
      `/new-conversations/${conversationId}/inbox`,
    );
    return success ? data?.pending ?? [] : null;
  }

  /**
   * Take one message back from the inbox before sending it on as an ordinary
   * message. true: this caller took it; false: a turn already read it; null: the call failed.
   */
  public async consumeInboxItem(conversationId: string, itemId: string): Promise<boolean | null> {
    const { success, data } = await ApiService.delete<{ consumed?: boolean }>(
      `/new-conversations/${conversationId}/inbox/${encodeURIComponent(itemId)}`,
    );
    return success ? Boolean(data?.consumed) : null;
  }

  /**
   * Check whether a conversation has an active in-flight worker execution.
   */
  public async getStreamStatus(conversationId: string): Promise<{
    isActive: boolean;
    status?: string;
    lastEventId?: number;
    turnId?: string;
  }> {
    try {
      const { success, data } = await ApiService.get<{
        is_active?: boolean;
        status?: string;
        last_seq?: number;
        turn_id?: string;
      }>(`/new-conversations/${conversationId}/status`);
      if (success && data) {
        return {
          isActive: Boolean(data.is_active || data.status === 'in_progress'),
          status: data.status,
          lastEventId: data.last_seq,
          turnId: data.turn_id,
        };
      }
      return { isActive: false };
    } catch {
      return { isActive: false };
    }
  }

  /**
   * The Ask card's resolution hook — a guaranteed verdict (resolved / open /
   * parked) on the memo's question after an inline answer. Floors to `open` on
   * any failure so the card never strands (the composer just stays).
   */
  public async memoResolution(
    conversationId: string,
    body: {
      nudge_id: string;
      question: string;
      turns: Array<{ role: 'user' | 'leo'; text: string }>;
    },
  ): Promise<{ state: 'resolved' | 'open' | 'parked'; reason?: string }> {
    try {
      const { success, data } = await ApiService.post<
        typeof body,
        { state: string; reason?: string }
      >(`/new-conversations/${conversationId}/memo-resolution`, body);
      if (
        success &&
        (data?.state === 'resolved' || data?.state === 'open' || data?.state === 'parked')
      ) {
        return data as { state: 'resolved' | 'open' | 'parked'; reason?: string };
      }
    } catch (e) {
      console.warn('[NewConversationService.memoResolution] failed, defaulting open:', e);
    }
    return { state: 'open' };
  }

  public async submitFeedback(conversationId: string, value: 0 | 1, messageId?: string, comment?: string): Promise<string | null> {
    try {
      const { success, data } = await ApiService.post<
        { value: number; message_id?: string; comment?: string },
        { ok: boolean; score_id: string }
      >(`/new-conversations/${conversationId}/feedback`, {
        value,
        message_id: messageId,
        comment,
      });
      if (success && data?.score_id) {
        return data.score_id;
      }
      return null;
    } catch (e) {
      console.warn('[NewConversationService.submitFeedback] Error:', e);
      return null;
    }
  }

  public async updateFeedbackComment(conversationId: string, scoreId: string, comment: string): Promise<void> {
    try {
      await ApiService.patch(`/new-conversations/${conversationId}/feedback/${scoreId}`, {
        comment,
      });
    } catch (e) {
      console.warn('[NewConversationService.updateFeedbackComment] Error:', e);
    }
  }

  public async getAnnotations(conversationId: string): Promise<ResponseAnnotation[]> {
    const { success, data, error } = await ApiService.get<{ annotations: ResponseAnnotation[] }>(
      `/new-conversations/${conversationId}/annotations`,
    );
    if (!success) throw new Error(formatError(error) || 'Failed to load annotations');
    return data?.annotations ?? [];
  }

  public async saveAnnotation(
    conversationId: string,
    messageTimestamp: string,
    annotation: ResponseAnnotationInput,
  ): Promise<ResponseAnnotation> {
    const { success, data, error } = await ApiService.put<ResponseAnnotationInput, ResponseAnnotation>(
      `/new-conversations/${conversationId}/annotations/${encodeURIComponent(messageTimestamp)}`,
      annotation,
    );
    if (!success || !data) throw new Error(formatError(error) || 'Failed to save annotation');
    return data;
  }

  public async deleteAnnotation(conversationId: string, messageTimestamp: string): Promise<void> {
    const { success, error } = await ApiService.delete(
      `/new-conversations/${conversationId}/annotations/${encodeURIComponent(messageTimestamp)}`,
    );
    if (!success) throw new Error(formatError(error) || 'Failed to delete annotation');
  }

  public stream(conversationId: string, operationId: string): EventSource {
    // NOTE: This method is deprecated. Streaming is now handled directly 
    // in the create() method when enable_streaming is true.
    // This method is kept for backward compatibility but will not work
    // since the /stream endpoint has been removed.
    console.warn('NewConversationService.stream() is deprecated. Use create() with enable_streaming: true instead.');
    
    const path = `/new-conversations/${conversationId}/stream?operation_id=${encodeURIComponent(
      operationId,
    )}`;
    const absolute = `${API_BASE_URL}${path}`;
    return new EventSource(absolute);
  }

  /**
   * Stream message using SSE (POST method)
   * Returns async generator that yields chunks
   */
  /**
   * Stream message with SSE reconnection support.
   * 
   * @param conversationId - The conversation ID to stream
   * @param message - The message to send
   * @param uploadedImages - Optional uploaded images
   * @param isRetry - Whether this is a retry request
   * @param lastEventId - Last event ID received (for reconnection via Last-Event-ID header)
   * @param signal - Optional AbortSignal to cancel the stream
   * @yields SSE events with their event IDs for tracking reconnection state
   */
  public async *streamMessage(
    conversationId: string,
    message: string,
    uploadedImages?: any[],
    isRetry?: boolean,
    lastEventId?: number,
    isHidden?: boolean,
    signal?: AbortSignal,
    displayText?: string,
    slackSimulation?: { enabled: boolean; async?: boolean },
    agentArchitecture?: 'classic' | 'code' | 'code_simple',
    askCard?: boolean,
    requestContext?: { headers: Readonly<Record<string, string>>; onRequest: () => void; sendId?: string },
  ): AsyncGenerator<SSEStreamEvent, void, unknown> {
    if (isExecutiveConversationId(conversationId) && !requestContext?.sendId) {
      throw new Error('Executive sends require a client message ID.');
    }
    const { API_BASE_URL } = await import('@config/config');
    // The typed message's id rides the URL, not the body: a send refused as
    // unauthenticated is rejected before any body is read, and this is what
    // lets the server log that refusal against the message it belonged to.
    const sendQuery = requestContext?.sendId ? `?send_id=${encodeURIComponent(requestContext.sendId)}` : '';
    const url = `${API_BASE_URL}/new-conversations/${conversationId}/stream${sendQuery}`;

    // Prepare request body
    const requestBody: any = { message };
    if (uploadedImages && uploadedImages.length > 0) {
      requestBody.uploaded_images = uploadedImages;
    }
    if (isRetry) {
      requestBody.is_retry = true;
    }
    if (isHidden) {
      requestBody.is_hidden = true;
    }
    if (displayText) {
      requestBody.display_text = displayText;
    }
    // Internal-user Slack-sim toggle — injects SLACK_CONTEXT triggers
    // server-side so the prompt paradigms fire without a real channel.
    if (slackSimulation?.enabled) {
      requestBody.slack_simulation = {
        enabled: true,
        async: !!slackSimulation.async,
      };
    }
    // Internal-user code-mode toggle — routes this turn to the parallel
    // code-execution agent (gated server-side by is_internal_email). 'code' = full
    // code mode; 'code_simple' = the primitives-only variant.
    if (agentArchitecture === 'code' || agentArchitecture === 'code_simple') {
      requestBody.agent_architecture = agentArchitecture;
    }
    // Ask-card read-only marker (conv_memo_ replies from MemoChat). Drives the backend's
    // read-only code-mode surface for THIS SEND specifically — not the conv id — so a panel
    // continuation of the same conversation ("Continue in chat") runs full Leo. Absent on
    // every other send (undefined → omitted → full).
    if (askCard) {
      requestBody.ask_card = true;
    }

    const headers = requestContext ? { ...requestContext.headers } : attachAuthHeaders({ 'Content-Type': 'application/json' });
    // Last-Event-ID lets the backend skip events we've already received.
    if (lastEventId !== undefined && lastEventId > 0) {
      headers['Last-Event-ID'] = String(lastEventId);
    }

    const body = JSON.stringify(requestBody);
    const response = await fetchWithSessionRefresh(
      () => {
        requestContext?.onRequest();
        return fetch(url, {
          method: 'POST',
          headers,
          credentials: 'include',
          body,
          signal,
        });
      },
      () => ApiService.refreshSession(),
    );

    if (!response.ok) {
      // HTTP/2 carries no reason phrase, so `statusText` is "" on this ALB
      // (routing.http2.enabled). The numeric status is the only thing that
      // survives, and it is what the retry classifiers match a 502/504 on.
      const { message, code } = await this.extractErrorFromResponse(
        response,
        `Stream failed: ${response.status}${response.statusText ? ` ${response.statusText}` : ''}`
      );
      
      const error = new Error(message);
      if (code) {
        (error as any).code = code;
      }
      // The status is the only reliable discriminator here — the message is
      // whatever the origin of the failure happened to write, and for an ALB
      // 502 that is an HTML page we did not author.
      (error as any).status = response.status;

      throw error;
    }

    yield* this.iterateSSE(response);
  }

  /**
   * A mid-turn steer to an executive conversation: POST /stream, read only the
   * `executive_steer` ack (apis/executiveContract.ts), then close. The running
   * turn's own tail carries the rest. Sent once — never retried here; a refusal
   * comes back as an Error with the server's `status`/`code` for the tab to show.
   */
  public async steerExecutive(conversationId: string, message: string, sendId: string): Promise<ExecutiveSteerAck> {
    const controller = new AbortController();
    const key = `${executiveResourceKey(conversationId)}:${sendId}`;
    executiveSteerControllers.set(key, controller);
    try {
      const response = await fetchWithSessionRefresh(
        () => fetch(`${API_BASE_URL}/new-conversations/${conversationId}/stream?send_id=${encodeURIComponent(sendId)}`, {
          method: 'POST',
          headers: attachAuthHeaders({ 'Content-Type': 'application/json' }),
          credentials: 'include',
          body: JSON.stringify({ message }),
          signal: controller.signal,
        }),
        () => ApiService.refreshSession(),
      ).catch(error => {
        throw Object.assign(error instanceof Error ? error : new Error(String(error)),
          { status: 503, code: 'executive_unavailable', sendId, requestSent: null });
      });
      if (!response.ok) {
        const identity = await response.clone().json().catch(() => ({}));
        const { message: text, code } = await this.extractErrorFromResponse(response, `Stream failed: ${response.status}`);
        throw Object.assign(new Error(text), { status: response.status, code,
          sendId, msgId: identity.msg_id, requestSent: identity.request_sent });
      }
      try {
        for await (const { data } of this.iterateSSE(response)) {
          if ((data as { type?: unknown })?.type === 'keepalive') continue;
          const ack = parseSteerAck(data, Date.now());
          if (ack) return ack;
          break;
        }
        throw Object.assign(new Error('Executive unavailable: this message was not acknowledged; delivery is uncertain.'),
          { status: 503, code: 'executive_unavailable', sendId, requestSent: null });
      } catch (error) {
        throw Object.assign(error instanceof Error ? error : new Error(String(error)),
          { status: 503, code: 'executive_unavailable', sendId, requestSent: null });
      }
    } finally {
      controller.abort();
      if (executiveSteerControllers.get(key) === controller) executiveSteerControllers.delete(key);
    }
  }

  /**
   * Read-only SSE TAIL of an existing run's stream (GET /:id/subscribe).
   *
   * The consumer side of the async-task streaming substrate: it never sends a
   * message or enqueues a turn — it just replays + tails the durable Redis stream
   * for `conversationId` (for an async board run, `async_{task_id}`). Yields the
   * SAME `{ eventId, data }` shape as `streamMessage`, so the same event-handling
   * pipeline can drive a factory chatbot store and render the run live.
   *
   * Gated server-side by LEO_UNIFIED_INTERRUPTS (404 when OFF).
   */
  public async *subscribeStream(
    conversationId: string,
    lastEventId?: number,
    signal?: AbortSignal,
    requestContext?: { headers: Readonly<Record<string, string>>; onRequest: () => void },
  ): AsyncGenerator<SSEStreamEvent, void, unknown> {
    const { API_BASE_URL } = await import('@config/config');
    const params =
      lastEventId !== undefined && lastEventId > 0
        ? `?last_event_id=${lastEventId}`
        : '';
    const url = `${API_BASE_URL}/new-conversations/${conversationId}/subscribe${params}`;

    const headers = requestContext ? { ...requestContext.headers } : attachAuthHeaders({});
    if (lastEventId !== undefined && lastEventId > 0) {
      headers['Last-Event-ID'] = String(lastEventId);
    }

    const response = await fetchWithSessionRefresh(
      () => {
        requestContext?.onRequest();
        return fetch(url, {
          method: 'GET',
          headers,
          credentials: 'include',
          signal,
        });
      },
      () => ApiService.refreshSession(),
    );

    if (!response.ok) {
      // Same shape as streamMessage and streamQuestionnaire: HTTP/2 sends no
      // reason phrase so statusText is "", and this is the RECONNECT path —
      // the one a gateway error is most likely to hit, since it runs after a
      // drop rather than on a fresh user action.
      const { message, code } = await this.extractErrorFromResponse(
        response,
        `Subscribe failed: ${response.status}${response.statusText ? ` ${response.statusText}` : ''}`,
      );
      const error = new Error(message);
      if (code) {
        (error as any).code = code;
      }
      (error as any).status = response.status;
      throw error;
    }

    yield* this.iterateSSE(response);
  }

  /**
   * `reader.read()` that rejects instead of hanging when the connection stalls.
   *
   * Rejecting is the point: the caller's `catch` is what arms reconnection, so a
   * stall has to look like the network error it is. Returning `done` would be
   * indistinguishable from the server finishing the turn. The rejection carries
   * `isStall` so the caller can limit these expensive timeout-driven retries
   * separately from brief network failures.
   *
   * `armed` exists because the deadline is only meaningful against a server
   * that promises to fill silence. The worker-backed stream sends a keepalive
   * every 30s; the direct in-Flask path (BACKGROUND_WORKER_ENABLED=false, the
   * local-dev default) sends none, and there a quiet minute is a slow tool
   * call, not a dead socket. Callers arm this once they have SEEN a keepalive,
   * so the client discovers which contract it is under instead of assuming.
   */
  private async readWithStallTimeout(
    reader: ReadableStreamDefaultReader<Uint8Array>,
    armed: boolean,
  ) {
    if (!armed) {
      return reader.read();
    }
    let stallTimer: ReturnType<typeof setTimeout> | undefined;
    try {
      return await Promise.race([
        reader.read(),
        new Promise<never>((_, reject) => {
          stallTimer = setTimeout(
            () => {
              const stallError = new Error(
                `connection stalled: no stream data for ${SSE_STALL_TIMEOUT_MS / 1000}s`,
              );
              // Count stalls separately: each has already cost this full
              // timeout before the recovery window can begin.
              (stallError as any).isStall = true;
              reject(stallError);
            },
            SSE_STALL_TIMEOUT_MS,
          );
        }),
      ]);
    } catch (err) {
      // Free the socket; the body is never read again on this path.
      reader.cancel().catch(() => {});
      throw err;
    } finally {
      clearTimeout(stallTimer);
    }
  }

  /**
   * Parse a `text/event-stream` Response body into `{ eventId, data }` events.
   * Shared by `streamMessage` (POST send) and `subscribeStream` (GET tail) so the
   * two stay byte-identical in how they decode id:/data:/keepalive lines.
   */
  private async *iterateSSE(
    response: Response,
  ): AsyncGenerator<SSEStreamEvent, void, unknown> {
    const reader = response.body?.getReader();
    const decoder = new TextDecoder();
    let buffer = '';
    let currentEventId = 0;
    // See readWithStallTimeout: only a server that has actually sent a
    // keepalive has promised to keep sending them, and only then does silence
    // prove the connection is dead rather than the agent busy.
    let keepaliveContractSeen = false;

    if (!reader) {
      throw new Error('No reader available');
    }

    while (true) {
      const { done, value } = await this.readWithStallTimeout(reader, keepaliveContractSeen);
      if (done) break;

      buffer += decoder.decode(value, { stream: true });
      const lines = buffer.split('\n');
      buffer = lines.pop() || '';

      for (const line of lines) {
        // Parse SSE event ID line (format: "id: <number>")
        if (line.startsWith('id: ')) {
          const parsedId = parseInt(line.slice(4), 10);
          if (!isNaN(parsedId)) {
            currentEventId = parsedId;
          }
        }
        // Handle SSE keepalive comments (format: ": <comment>")
        else if (line.startsWith(':')) {
          keepaliveContractSeen = true;
          yield { eventId: currentEventId, data: { type: 'keepalive' } };
        }
        // Parse SSE data line (format: "data: <json>")
        else if (line.startsWith('data: ')) {
          try {
            const data = JSON.parse(line.slice(6));
            // Yield both the event ID and data for reconnection tracking
            yield { eventId: currentEventId, data };
          } catch (e) {
            console.warn('Failed to parse SSE data:', line, e);
          }
        }
        // Ignore SSE comments (format: ": <comment>") - these are keepalives
      }
    }
  }
}

export default new NewConversationService();
